import hoistNonReactStatic from 'hoist-non-react-statics';
import React, { ComponentType, ReactElement } from 'react';
import getDisplayName from 'react-display-name';

class RenderInsteadContainer {
    constructor(public element: ReactElement | null) {}
}

/**
 * Для использования хуков в классах, чтобы не переписывать целиком.
 * Работает похоже на mapStateToProps.
 *
 * @example
 * const useExtraProps = () => {
 *     return {
 *         large: useMedia('(min-width: 1240px)'),
 *     };
 * };
 *
 * const ComponentWithMedia = withHookProps(useExtraProps)(Component);
 */
export function withHookProps<TOwn, PExtra, MapperReturnType = undefined>(
    useExtraProps: (ownProps: TOwn | MapperReturnType) => PExtra | RenderInsteadContainer,
    { mapOwnProps, hookPropKey }: { mapOwnProps?: (ownProps: TOwn) => MapperReturnType; hookPropKey?: string } = {}
) {
    return function withHookPropsHOC<Props extends TOwn & PExtra>(WrappedComponent: ComponentType<Props>) {
        const WithHookProps = (ownProps: Props) => {
            const mappedProps = mapOwnProps?.(ownProps) ?? ownProps;
            const extraProps = useExtraProps(mappedProps);

            if (extraProps instanceof RenderInsteadContainer) {
                return extraProps.element;
            }

            const props = { ...ownProps, ...(hookPropKey ? { [hookPropKey]: extraProps } : extraProps) } as Props;

            return <WrappedComponent {...(props as any)} />;
        };

        hoistNonReactStatic(WithHookProps, WrappedComponent);

        WithHookProps.displayName = `WithHookProps(${getDisplayName(WrappedComponent)})`;

        return WithHookProps;
    };
}

withHookProps.renderInstead = (element: RenderInsteadContainer['element']) => {
    return new RenderInsteadContainer(element);
};
